<script setup lang="ts">
import useWorkspace from "@/composition/useWorkspace";
import { inject, nextTick, onMounted, onUnmounted, ref, UnwrapRef, useTemplateRef, watch } from "vue";
import useBackend from "@/composition/useBackend";
import { personalFolderId, Query, QueryColumn, QueryFolder, QueryNavigation, sharedFolderId } from "@/backend/queries";
import { Pane, TabView } from "./workspace-types";
import { v7 } from "uuid";
import {
  legacyModeKey,
  useContext,
  useDialogs,
  useLocalization,
  usePhrases,
  ViewContext,
  ViewOptions
} from "@hantera/portal-app";
import { ContextMenuSection, IconChevron, Tree, TreeNode } from "@hantera/design-system";
import QuickSearch from "@/components/QuickSearch";
import ProfileBar from "./ProfileBar.vue";
import useApps from "@/composition/use-apps";
import log from "@/log";

const phoneBreakpoint = 768;

const workspace = useWorkspace();
const context = useContext()
const t = useLocalization();
const { graph, queries } = useBackend();
const dialogs = useDialogs();
const legacyMode = inject(legacyModeKey, false);
const tree = useTemplateRef('tree');
const apps = useApps()
const { authorizeAll, openView } = useContext()
const phrases = usePhrases('navHub')

if (typeof workspace.state.sidebarWidth === 'undefined') {
  workspace.state.sidebarWidth = 230;
}

// We need an internal variable for the sidebar width to update during dragging to avoid saving to localStorage.
const sidebarWidth = ref(workspace.state.sidebarWidth);
watch(() => workspace.state.sidebarWidth, () => {
  sidebarWidth.value = workspace.state.sidebarWidth;
});

onMounted(() => {
  addEventListener('resize', refreshWidth)
});
onUnmounted(() => {
  removeEventListener('resize', refreshWidth)
});

const style = ref<Record<string, string>>({});

watch(sidebarWidth, refreshWidth, { immediate: true });

function refreshWidth() {
  if (sidebarWidth.value < 100) {
    sidebarWidth.value = 100;
  }
  const maxWidth = window.innerWidth * 0.60;
  if (sidebarWidth.value > maxWidth) {
    sidebarWidth.value = maxWidth;
  }

  if (window.innerWidth < phoneBreakpoint) {
    style.value.width = '100%';
  } else {
    style.value.width = sidebarWidth.value + 'px';
  }
}

watch(() => workspace.state.showSidebar, () => {
  if (!workspace.state.showSidebar) {
    style.value.maxWidth = sidebarWidth.value + 'px';
    setTimeout(() => {
      style.value.maxWidth = '0';
    }, 0);
    return;
  }

  if (window.innerWidth < phoneBreakpoint) {
    style.value.maxWidth = '100%';
    return;
  }

  style.value.maxWidth = sidebarWidth.value + 'px';
  setTimeout(() => {
    delete style.value.maxWidth;
  }, 200);
}, { immediate: true });

const nodes = ref<TreeNode[]>([]);

watch(() => [queries.folders, queries.queries], () => {
  updateNodes();
}, {
  immediate: true,
  deep: true
});

watch(() => workspace.state.panes, () => {
  if (window.innerWidth < phoneBreakpoint) {
    // Tabs changed, and we're on a phone/small device - close sidebar
    workspace.state.showSidebar = false;
  }
}, { deep: true });
function closeIfPhone() {
  if (window.innerWidth < phoneBreakpoint) {
    workspace.state.showSidebar = false;
  }
}

function openOrReplaceView(path: string, options: ViewOptions) {
  let pane: Pane | undefined;

  if (workspace.state.activePaneId) {
    pane = workspace.state.panes.find(r => r.paneId === workspace.state.activePaneId);
  }
  if (!pane) {
    pane = workspace.state.panes[0];
  }

  if (!pane) { return; }



  const existing = pane.tabs.find(r => (r as TabView).path === path);

  if (existing) {
    pane.selectedTabId = existing.tabId;

    closeIfPhone();
    return;
  }

  openView(path, options)
}

function updateNodes() {

  nodes.value = [];

  nodes.value.push(<any>{
    id: 'sales',
    label: 'Sales',
    style: 'header',
    sort: 'none',
    children: [{
      id: 'sales-orders',
      label: 'Orders',
      onClick() {
        openOrReplaceView('/sales/orders', {})
      }
    }, {
      id: 'sales-deliveries',
      label: 'Deliveries',
      // tags: [{
      //   value: 10,
      //   style: 'secondary',
      //   title: 'Open'
      // },{
      //   value: 549,
      //   style: 'hue',
      //   title: 'Picking',
      //   hue: 202
      // }],
      onClick() {
        openOrReplaceView('/sales/deliveries', {})
      }
    }, {
      id: 'sales-invoices',
      label: 'Invoices',
      onClick() {
        openOrReplaceView('/sales/invoices', {})
      }
    }, {
      id: 'sales-skus',
      label: 'Inventory',
      onClick() {
        openOrReplaceView('/sales/inventory', {})
      }
    }
      // ,{
      //   id: 'sales-payments',
      //   label: 'Payments',
      //   onClick() {
      //     openView('/sales/payments', {})
      //   }
      // }
    ]
  })

  nodes.value.push(<any>{
    id: 'queries',
    label: 'Queries',
    style: 'header',
    children: queries.folders.map(f => queryFolderToTreeNode(f))
  })

  nodes.value.push(<any>{
    id: 'reports',
    label: 'Reports',
    style: 'header',
    children: []
  })

  // nodes.value.push(<any>{
  //   id: 'marketing',
  //   label: 'Marketing',
  //   style: 'header',
  //   children: [{
  //     id: 'promotion-periods',
  //     label: 'Promotion Periods',
  //     onClick() {
  //       if (workspace.openViewTab('/marketing/promotion-periods', 'Promotion Periods')) {
  //         closeIfPhone();
  //       }
  //     }
  //   }]
  // })

  const systemChildren = []

  // Jobs
  if (authorizeAll(['jobs:read', 'job-definitions:read'])) {
    systemChildren.push({
      id: 'settings-jobs',
      label: 'Jobs',
      onClick() {
        openOrReplaceView('/jobs/dashboard', {})
      }
    })
  }

  // Any one of the tabs' permissions makes the view worth showing; requiring all of them would
  // hide it from the operators it exists for.
  if (authorizeAll(['traces:read'])
    || authorizeAll(['ingresses:statistics'])
    || authorizeAll(['egresses:statistics'])
    || authorizeAll(['jobs:statistics'])
    || authorizeAll(['ingresses:read'])
    || authorizeAll(['egresses:read'])) {
    systemChildren.push({
      id: 'system-monitoring',
      label: 'Monitoring',
      onClick() {
        openOrReplaceView('/system/monitoring', {})
      }
    })
  }


  // Apps (only if user has read permission)
  if (authorizeAll(['apps:read'])) {
    systemChildren.push({
      id: 'system-apps',
      label: 'Apps',
      onClick() {
        if (workspace.openViewTab('/system/apps', 'Apps')) {
          closeIfPhone();
        }
      }
    })
  }

  systemChildren.push({
    id: 'settings-audit',
    label: 'Audit Logs',
    onClick() {
      openOrReplaceView('/settings/audit', {})
    }
  })

  // IAM section (only show if user has any IAM permissions)
  const iamChildren = []

  if (authorizeAll(['iam/principals:read'])) {
    iamChildren.push({
      id: 'iam-principals',
      label: 'Users',
      onClick() {
        openOrReplaceView('/iam/principals', {})
      }
    })
  }

  if (authorizeAll(['iam/clients:read'])) {
    iamChildren.push({
      id: 'iam-clients',
      label: 'External Applications',
      onClick() {
        openOrReplaceView('/iam/clients', {})
      }
    })
  }

  if (authorizeAll(['iam/roles:read'])) {
    iamChildren.push({
      id: 'iam-roles',
      label: 'Roles',
      onClick() {
        openOrReplaceView('/iam/roles', {})
      }
    })
  }

  // Only add IAM section if user has at least one IAM permission
  if (iamChildren.length > 0) {
    systemChildren.push(<any>{
      id: 'iam',
      label: 'IAM',
      children: iamChildren
    })
  }

  const settingsChildren = []

  // Portal Phrases
  settingsChildren.push({
    id: 'settings-phrases',
    label: 'Portal Phrases',
    onClick() {
      openOrReplaceView('/settings/phrases', {})
    }
  })

  if (authorizeAll(['registry/inventories:read', 'registry/inventories:write'])) {
    settingsChildren.push({
      id: 'settings-inventories',
      label: 'Inventories',
      onClick() {
        openOrReplaceView('/settings/inventories', {})
      }
    })
  }

  if (authorizeAll(['registry/channels:read', 'registry/channels:write'])) {
    settingsChildren.push({
      id: 'settings-channels',
      label: 'Channels',
      onClick() {
        openOrReplaceView('/settings/channels', {})
      }
    })
  }

  if (authorizeAll(['registry/locales:read', 'registry/locales:read'])) {
    settingsChildren.push({
      id: 'settings-locales',
      label: 'Locales',
      onClick() {
        openOrReplaceView('/settings/locales', {})
      }
    })
  }

  if (authorizeAll(['registry/currencies:read', 'registry/currencies:write'])) {
    settingsChildren.push({
      id: 'settings-currencies',
      label: 'Currencies',
      onClick() {
        openOrReplaceView('/settings/currencies', {})
      }
    })
  }

  if (settingsChildren.length > 0) {
    systemChildren.push({
      id: 'settings',
      label: 'Settings',
      children: settingsChildren
    })
  }

  nodes.value.push(<any>{
    id: 'system',
    label: 'System',
    style: 'header',
    children: systemChildren
  })

  const appsSections = apps.getNavHubSections()
  let idx = 0
  for (const section of appsSections) {

    let parent = nodes.value.find(r => r.id === section.id)
    let newParent = false
    if (!parent) {
      newParent = true
      parent = {
        id: section.id,
        label: phrases(capitalize(section.id)),
        children: [],
        style: 'header' as UnwrapRef<TreeNode["style"]>,
      }
    }

    if (parent && !parent.children) {
      parent.children = []
    }

    for (const item of section.items) {

      if (!parent || !parent.children || !authorizeAll(item.accessPatterns)) {
        continue;
      }

      parent.children.push({
        id: `custom_${++idx}`,
        label: item.label,
        onClick: () => {
          item.action({
            openView(uri: string, options: ViewOptions): Readonly<ViewContext> {
              if (workspace.openViewTab(uri, item.label)) {
                closeIfPhone();
              }
              return {} as any
            }
          })
        }
      })
    }

    if (newParent && parent && parent.children && parent.children.length > 0) {
      // Insert before system menu
      nodes.value.splice(nodes.value.length - 1, 0, parent)
    }
  }
}

function capitalize(input: string) {
  input = input.replace(/((?<![\.\s])[A-Z])/g, ' $1');
  input = input.replace(/(\.\w)/g, (match) => '.' + match.charAt(1).toUpperCase());
  input = input.charAt(0).toUpperCase() + input.slice(1);
  return input;
}

function queryFolderToTreeNode(folder: QueryFolder, isShared = false): TreeNode {

  if (folder.id === sharedFolderId) {
    isShared = true;
  }

  const children = folder.children.map(f => queryFolderToTreeNode(f, isShared)) as TreeNode[];

  const childQueries = Object.values(queries.queries).filter(r => r.folderId === folder.id) || [];

  children.push(...childQueries.map(q => ({
    id: q.id,
    label: q.name,
    type: 'query',
    draggable: {
      type: 'query',
      data: {
        queryId: q.id,
      }
    },
    contextMenu: [
      [{
        label: t('workspace.deleteQuery'),
        async action() {

          if (await dialogs.confirm(
            'Are you sure you want to delete this query?' + (isShared ? ' This will delete the shared query for all users.' : ''),
            'Delete query',
            'Delete',
            'Cancel',
            true
          )) {
            queries.deleteQuery(q);
          }
        }
      }, {
        label: t('workspace.copyQueryDef'),
        async action() {

          await navigator.clipboard.writeText(JSON.stringify({
            _hanteraItem: 'query',
            name: q.name,
            navigation: q.navigation,
            columns: q.columns
          }))
        }
      }, {
        label: t('workspace.rename'),
        async action() {
          tree.value?.rename(q.id!);
        },
      }]
    ],
    onClick() {
      let pane: Pane | undefined;

      if (workspace.state.activePaneId) {
        pane = workspace.state.panes.find(r => r.paneId === workspace.state.activePaneId);
      }
      if (!pane) {
        pane = workspace.state.panes[0];
      }

      if (!pane) { return; }

      const existing = pane.tabs.find(r => (r as TabView).path === '/query' && (r as TabView).query?.id === q.id);

      if (existing) {
        pane.selectedTabId = existing.tabId;

        closeIfPhone();
        return;
      }

      workspace.openTab(pane.paneId, <TabView>{
        type: 'view',
        path: '/query',
        query: {
          id: q.id
        },
        count: 0,
        state: {
          showEditor: false,

          selections: {
            filters: {},
          }
        },
        tabId: v7()
      });

      closeIfPhone();
    },
    async onRename(newName) {
      return await queries.renameQuery(q, newName);
    },
  } as TreeNode)));

  const contextMenuNew = [{
    label: t('workspace.newQuery'),
    action() {
      newQueryTab(
        folder.id
      );
    }
  }, {
    label: t('workspace.newFolder'),
    async action() {
      const newFolder = await queries.createFolder(folder, 'New folder');

      if (newFolder === undefined) {
        return;
      }

      nextTick(() => {
        tree.value?.rename(newFolder.id);
      });
    }
  }, buildPasteQueryDefOption(folder.id)] as ContextMenuSection;

  const contextMenuEdit = [] as ContextMenuSection;

  if (folder.id !== sharedFolderId && folder.id !== personalFolderId) {
    contextMenuEdit.push({
      label: t('workspace.rename'),
      async action() {
        tree.value?.rename(folder.id);
      }
    });
    contextMenuEdit.push({
      label: t('workspace.delete'),
      async action() {

        // Check if folder is empty
        if (folder.children.length > 0 || childQueries.length > 0) {
          await dialogs.alert('Folder is not empty. Please delete all queries and subfolders first.', 'Folder not empty');
          return;
        }

        if (await dialogs.confirm(
          'Are you sure you want to delete this folder?' + (isShared ? ' This will delete the shared folder for all users.' : ''),
          'Delete folder',
          'Delete',
          'Cancel',
          true
        )) {
          await queries.deleteFolder(folder.id);
        }
      }
    });
  }

  let name = folder.name;
  if (folder.id === personalFolderId) {
    name = t('workspace.personalQueries');
  } else if (folder.id === sharedFolderId) {
    name = t('workspace.sharedQueries');
  }

  contextMenuNew.key = 'folderNew';
  contextMenuEdit.key = 'folderEdit';

  const contextMenu = [contextMenuNew];
  if (contextMenuEdit.length > 0) {
    contextMenu.push(contextMenuEdit);
  }

  return {
    id: folder.id,
    label: name + (childQueries.length > 0 ? ` (${childQueries.length})` : ''),
    children,
    type: 'queryFolder',
    draggable: {
      type: 'queryFolder',
      data: {
        queryFolderId: folder.id,
      }
    },
    dropZone: {
      accepts: ['query', 'queryFolder'],
      onDrop(payload) {
        if (payload.type === 'query') {
          queries.moveQuery(payload.data.queryId, folder.id)
        } else if (payload.type === 'queryFolder') {
          queries.moveFolder(payload.data.queryFolderId, folder.id)
        }
      },
    },
    async onRename(newName, context) {
      return await queries.renameFolder(context.node.id, newName);
    },
    contextMenu
  } as TreeNode;

  function buildPasteQueryDefOption(folderId: string) {
    return {
      label: t('workspace.pasteQueryDef'),
      async action() {

        const queryOpts = { name: 'clipboard-read', allowWithoutGesture: false };
        const permissionStatus = await navigator.permissions.query(queryOpts as any);

        if (permissionStatus.state === 'denied') {
          await dialogs.alert('Hantera has been denied access to read from clipboard, please check your browser settings to use this feature.')
          return
        }

        try {
          const clipboardContent = await navigator.clipboard.readText()
          const def = JSON.parse(clipboardContent)
          log.debug('Clipboard content:', def)

          if (def._hanteraItem === 'query') {

            const newQuery: Query = {
              name: getNextFreeName(def.name, 0),
              navigation: def.navigation as QueryNavigation,
              columns: def.columns as QueryColumn[],
              folderId
            }

            await queries.saveQuery(newQuery);
            updateNodes()

            function getNextFreeName(base: string, num: number) {
              let result = base
              if (num > 0) {
                result += ` (${num})`
              }

              const existing = childQueries.find(r => r.name === result)
              if (existing) {
                // Check if existing query ends with (<number>)
                const match = existing.name.match(/\((\d+)\)$/)
                if (match) {
                  const n = parseInt(match[1])
                  return getNextFreeName(base, n + 1)
                } else {
                  return getNextFreeName(base, 2)
                }
              } else {
                return result
              }
            }

          } else {
            await displayError()
          }

        } catch {
          await displayError()
        }

        async function displayError() {

          const queryOpts = { name: 'clipboard-read', allowWithoutGesture: false };
          const permissionStatus = await navigator.permissions.query(queryOpts as any);

          if (permissionStatus.state === 'denied') {
            await dialogs.alert('Hantera has been denied access to read from clipboard, please check your browser settings to use this feature.')
            return
          }

          await dialogs.alert('No query definition found in clipboard. Did you forget to copy first?')
        }
      }
    }
  }
}

function newQueryTab(folderId: string) {

  workspace.openTab(workspace.state.activePaneId ?? workspace.state.panes[0].paneId, <TabView>{
    type: 'view',
    path: '/query',
    count: 0,
    query: {
      id: 'new',
      folderId: folderId,
    },
    state: {
      showEditor: true,
      transientQuery: {
        name: t('workspace.newQuery'),
        folderId,
        columns: [],
        navigation: {}
      },

      selections: {
        filters: {},
      }
    },
    tabId: v7()
  });
}

// function getDropTarget(draggedNode: TreeNode, hoveredPath: TreeNode[]) {
//
//   if (draggedNode.type === 'query' || draggedNode.type === 'queryFolder') {
//     if (draggedNode.id === personalFolderId || draggedNode.id === sharedFolderId) {
//       // Root query folders can't be moved
//       return undefined;
//     }
//
//     return hoveredPath.find(r => r !== draggedNode && r.type === 'queryFolder');
//   }
//
//   return undefined;
// }
//
// function onTreeMove(node: TreeNode, newParent: TreeNode) {
//   if (node.type === 'query') {
//     queries.moveQuery(node.id, newParent.id);
//   } else if (node.type === 'queryFolder') {
//     queries.moveFolder(node.id, newParent.id);
//   }
// }

function hideSidebar() {
  workspace.state.showSidebar = false;
}

const isSizeDragging = ref(false);
let sizeDragOffset = 0;
function beginMouseSize(e: MouseEvent) {
  if (!workspace.state.showSidebar) {
    return;
  }
  e.stopPropagation();
  e.preventDefault();

  isSizeDragging.value = true;
  sizeDragOffset = sidebarWidth.value - e.clientX;

  document.addEventListener('mousemove', sizeMouse);
  document.addEventListener('mouseup', endSizeDrag);
}

function beginTouchSize(e: TouchEvent) {
  if (!workspace.state.showSidebar) {
    return;
  }
  e.stopPropagation();
  e.preventDefault();

  isSizeDragging.value = true;
  sizeDragOffset = sidebarWidth.value - e.touches[0].clientX;

  document.addEventListener('touchmove', sizeTouch);
  document.addEventListener('touchend', endSizeDrag);
}

function sizeMouse(e: MouseEvent) {
  sidebarWidth.value = e.clientX + sizeDragOffset;
}
function sizeTouch(e: TouchEvent) {
  sidebarWidth.value = e.touches[0].clientX + sizeDragOffset;
}

function endSizeDrag() {
  document.removeEventListener('mousemove', sizeMouse);
  document.removeEventListener('touchmove', sizeTouch);
  document.removeEventListener('mouseup', endSizeDrag);
  document.removeEventListener('touchend', endSizeDrag);

  workspace.state.sidebarWidth = sidebarWidth.value;
  isSizeDragging.value = false;
}

</script>

<template>
  <div class="sidebar" :class="{
    'sidebar--shown': workspace.state.showSidebar,
    'sidebar--resizing': isSizeDragging
  }" :style="style">
    <div class="sidebar__header">

      <div
        class="branding-logo"
      >
        <svg
          viewBox="0 0 127 26"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.90095 7.71353C6.48156 7.71353 4.54008 8.87842 3.10637 10.4017V0.724197H0V23.1259H3.10637V15.2405C3.25572 13.0003 4.74916 10.6706 7.70619 10.6706C9.88663 10.6706 11.3502 11.9848 11.3502 15.6288V23.1259H14.4566V14.2548C14.4566 10.0433 12.4554 7.71353 8.90095 7.71353Z"
            fill="currentColor"
          />
          <path
            d="M17.4862 15.5691C17.4862 19.9001 20.7419 23.4246 25.0729 23.4246C27.791 23.4246 29.9415 21.9312 30.8077 19.9897L30.9869 23.1259H33.944V8.01222H30.9869L30.8077 11.1186C29.9415 9.20698 27.791 7.71353 25.0729 7.71353C20.7419 7.71353 17.4862 11.2381 17.4862 15.5691ZM20.5925 15.5691C20.5925 12.8809 22.7132 10.6706 25.5807 10.6706C28.3883 10.6706 30.4792 12.8809 30.4792 15.5691C30.4792 18.2573 28.3883 20.4676 25.5807 20.4676C22.7132 20.4676 20.5925 18.2573 20.5925 15.5691Z"
            fill="currentColor"
          />
          <path
            d="M46.8777 7.71353C44.4284 7.71353 42.487 8.90829 41.0533 10.4316L40.9039 8.01222H37.9767V23.1259H41.0831V15.2405C41.2325 13.0003 42.7259 10.6706 45.6829 10.6706C47.8634 10.6706 49.327 11.9848 49.327 15.6288V23.1259H52.4333V14.2548C52.4333 10.0433 50.4321 7.71353 46.8777 7.71353Z"
            fill="currentColor"
          />
          <path
            d="M64.0442 8.01222H60.8183V3.53188H57.6821V8.01222H55.4419V10.9693H57.6821V23.1259H60.8183V10.9693H64.0442V8.01222Z"
            fill="currentColor"
          />
          <path
            d="M81.1498 15.7483C81.1498 11.4472 78.2525 7.71353 73.5929 7.71353C69.0528 7.71353 66.0361 11.2679 66.0361 15.4795C66.0361 19.9897 69.232 23.4246 73.8319 23.4246C77.8941 23.4246 80.2537 20.6767 80.7615 18.5858L78.0434 18.2871C77.4161 19.452 76.0422 20.6468 73.8617 20.6468C70.9645 20.6468 69.232 18.8547 69.1424 16.5249H81.1498V15.7483ZM73.5929 10.4914C76.2214 10.4914 77.894 12.3134 78.0135 14.3444H69.1424C69.2619 12.2238 71.0541 10.4914 73.5929 10.4914Z"
            fill="currentColor"
          />
          <path
            d="M90.9372 7.71353C89.623 7.71353 88.488 8.54986 87.5322 9.77449L87.3828 8.01222H84.4557V23.1259H87.562V15.9872C87.562 12.9705 88.6074 10.6706 90.758 10.6706C91.4151 10.6706 92.0125 10.9095 92.3112 11.0887L92.7891 8.37065C92.4605 8.07196 91.8333 7.71353 90.9372 7.71353Z"
            fill="currentColor"
          />
          <path
            d="M94.2121 15.5691C94.2121 19.9001 97.4678 23.4246 101.799 23.4246C104.517 23.4246 106.667 21.9312 107.534 19.9897L107.713 23.1259H110.67V8.01222H107.713L107.534 11.1186C106.667 9.20698 104.517 7.71353 101.799 7.71353C97.4678 7.71353 94.2121 11.2381 94.2121 15.5691ZM97.3184 15.5691C97.3184 12.8809 99.4391 10.6706 102.307 10.6706C105.114 10.6706 107.205 12.8809 107.205 15.5691C107.205 18.2573 105.114 20.4676 102.307 20.4676C99.4391 20.4676 97.3184 18.2573 97.3184 15.5691Z"
            fill="currentColor"
          />
          <path
            d="M114.049 23.135H126.001V25.2758H114.049V23.135Z"
            fill="currentColor"
          />
        </svg>
      </div>
      
      <button class="btn-silent sidebar__hide-button" @click="hideSidebar">
        <IconChevron />
      </button>
    </div>

    <div class="sidebar__search">
      <QuickSearch />
    </div>
    
    <div class="sidebar__body neat-scroll">
      
<!--      <SalesModule />-->
      
      <Tree
        cache-key="workspace-sidebar"
        :nodes="nodes"
        ref="tree"
      />
    </div>
    
    <ProfileBar />

    <div
      class="sidebar__separator"
      @mousedown="beginMouseSize"
      @touchstart="beginTouchSize"
      :class="{
        'sidebar__separator--dragging': isSizeDragging
      }"
    />
  </div>
</template>

<style scoped>
.sidebar {
  background-color: hsl(var(--color-90));
  border-right: 1px solid hsl(var(--color-60));
  height: 100%;
  position: relative;

  display: grid;
  grid-template-rows: min-content min-content auto min-content;
  transition: max-width 200ms;
  overflow: hidden;
}

.sidebar--resizing {
  overflow: visible;
}

.sidebar__search {
  padding: 0 5px 5px 5px;
}

.sidebar__body {
  overflow-x: auto;
  display: flex;
  flex-direction: column;
}

.sidebar__separator {
  position: absolute;
  top: 0;
  right: -3px;
  bottom: 0;
  width: 6px;
  cursor: ew-resize;
  background-color: hsla(var(--color-60), 0);
  transition: background-color 200ms;
  z-index: 100;
}

.sidebar__separator--dragging {
  background-color: hsla(var(--color-60), 1);
}

.sidebar__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  height: 35px;
}

.branding-logo {
  height: 35px;
  padding-left: 5px;
  width: calc(100% - 40px);
  overflow: hidden;
  display: flex;
  align-items: center;
}

.branding-logo svg {
  width: 100px;
  height: 20px;
}

.sidebar__hide-button {
  height: 22px;
  width: 22px;
  padding: 0;
  margin-right: 5px;
  --btn-border-radius: 4px;
}

.sidebar__hide-button svg {
  width: 12px;
  transform: rotate(90deg);
}

@media (max-width: 767px) {
  .sidebar__separator {
    display: none;
  }
}
</style>
